/**
 * Quick Create Event Dialog
 * Collects title and quick description before opening the full editor
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import { useCreateCalendarEvent } from '@/features/calendarEvents/hooks/useCalendarEvents'
import { EventOwnerEntityType } from '@/api/types/calendarEvent'

interface CreateEventDialogProps {
  open: boolean
  onClose: () => void
  groupId: string
  groupName?: string
}

export function CreateEventDialog({
  open,
  onClose,
  groupId,
  groupName,
}: CreateEventDialogProps) {
  const navigate = useNavigate()
  const createEventMutation = useCreateCalendarEvent()

  const [title, setTitle] = useState('')
  const [quickDescription, setQuickDescription] = useState('')
  const [errors, setErrors] = useState<{ title?: string; quickDescription?: string; submit?: string }>({})

  const validate = (): boolean => {
    const newErrors: { title?: string; quickDescription?: string } = {}

    if (!title.trim()) {
      newErrors.title = 'Title is required'
    } else if (title.length > 100) {
      newErrors.title = 'Title must be 100 characters or less'
    }

    if (!quickDescription.trim()) {
      newErrors.quickDescription = 'Quick description is required'
    } else if (quickDescription.length > 150) {
      newErrors.quickDescription = 'Quick description must be 150 characters or less'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleCreate = async () => {
    if (!validate()) return

    // Create default start/end times
    const now = new Date()
    const startDateTime = new Date(now)
    startDateTime.setHours(startDateTime.getHours() + 1, 0, 0, 0)
    const endDateTime = new Date(startDateTime)
    endDateTime.setHours(endDateTime.getHours() + 2)

    try {
      const result = await createEventMutation.mutateAsync({
        title: title.trim(),
        quickDescription: quickDescription.trim(),
        startDateTime: startDateTime.toISOString(),
        endDateTime: endDateTime.toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        isPublic: true,
        ownerEntityId: groupId,
        ownerEntityType: EventOwnerEntityType.Group,
      })

      // Navigate to edit page for the new event
      const eventId = result?.data?.id
      if (eventId) {
        onClose()
        navigate(`/events/${eventId}/edit`)
      }
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        submit: error instanceof Error ? error.message : 'Failed to create event',
      }))
    }
  }

  const handleClose = () => {
    setTitle('')
    setQuickDescription('')
    setErrors({})
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Create Event</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        {groupName && (
          <Typography variant="body2" color="text.secondary">
            For {groupName}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent>
        {errors.submit && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrors((prev) => ({ ...prev, submit: undefined }))}>
            {errors.submit}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Event Title"
            required
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            error={!!errors.title}
            helperText={errors.title || `${title.length}/100`}
            inputProps={{ maxLength: 100 }}
            autoFocus
            disabled={createEventMutation.isPending}
          />

          <TextField
            label="Quick Description"
            required
            fullWidth
            multiline
            rows={2}
            value={quickDescription}
            onChange={(e) => setQuickDescription(e.target.value)}
            error={!!errors.quickDescription}
            helperText={errors.quickDescription || `${quickDescription.length}/150 - A short summary shown in event listings`}
            inputProps={{ maxLength: 150 }}
            disabled={createEventMutation.isPending}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={createEventMutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={createEventMutation.isPending || !title.trim() || !quickDescription.trim()}
          startIcon={createEventMutation.isPending ? <CircularProgress size={16} /> : undefined}
        >
          {createEventMutation.isPending ? 'Creating...' : 'Create & Edit'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
